import React, { useMemo, useState } from 'react';
import type { RolPermiso } from '../types';
import { PageShell, glassLight, primaryButton, ghostButton, EmptyState, MotionGrid, MotionItem, useIsMobile, usePagination, Pagination, T } from '../ui';
// Módulos actuales del sistema (orden de despliegue). Cualquier permiso viejo
// guardado para módulos que ya no existen (mapa, equipos, empleados, etc.) no
// se muestra.
// Debe mantenerse alineada con MODULE_KEYS de App.tsx: un módulo que falte
// aquí se puede otorgar en el editor de roles pero no se ve en esta matriz.
const MODULOS_ACTUALES = ['inicio', 'consumo', 'costoConsumo', 'sucursales', 'reportes', 'alertas', 'usuarios', 'roles', 'gas', 'pedidos', 'pedidosAutorizar', 'reportesPedidos', 'flota', 'auditoria', 'configuracion'] as const;
const moduleLabels: Record<string, string> = {
  inicio: 'Panel',
  consumo: 'Consumo',
  costoConsumo: 'Ver costo estimado',
  sucursales: 'Sucursales',
  reportes: 'Reportes',
  alertas: 'Alertas',
  usuarios: 'Usuarios',
  roles: 'Roles',
  gas: 'Gas Industrial',
  pedidos: 'Órdenes de Pedido',
  pedidosAutorizar: 'Autorizar órdenes',
  reportesPedidos: 'Reportes de Órdenes',
  flota: 'Flota y Destinos',
  auditoria: 'Auditoría',
  configuracion: 'Configuración'
};
const sinPermiso = {
  leer: false,
  editar: false,
  archivar: false
};
export function RolesPermisos({
  roles,
  onNew,
  onEdit,
  onDelete,
  canArchive
}: {
  roles: RolPermiso[];
  onNew: () => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  canArchive: boolean;
}) {
  const [tab, setTab] = useState<'activos' | 'archivados'>('activos');
  const isMobile = useIsMobile();
  const filteredRoles = useMemo(() => {
    return roles.filter(r => tab === 'activos' ? !r.archivado : r.archivado);
  }, [roles, tab]);
  const {
    page,
    setPage,
    totalPages,
    pageItems,
    rangeStart,
    rangeEnd,
    total
  } = usePagination(filteredRoles);
  return <PageShell title="Roles y permisos" sub="Control de accesos por perfil del sistema" action={<button onClick={onNew} style={primaryButton} data-id="element-1196" data-mp="ebmzo5i3rbkh">
          + Nuevo rol
        </button>} data-id="element-1195" data-mp="3763l4wbe34bd">
      
      <div style={{
      display: 'flex',
      gap: 8,
      marginBottom: 20
    }} data-id="element-1197" data-mp="9dlglkkofc90">
        
        <button onClick={() => {
        setTab('activos');
        setPage(1);
      }} style={{
        ...ghostButton,
        background: tab === 'activos' ? T.accentSoft : 'transparent',
        color: tab === 'activos' ? T.accent : T.textMuted
      }} data-id="element-1198" data-mp="n500dtmerhh4">
          
          Activos
        </button>
        {canArchive && <button onClick={() => {
        setTab('archivados');
        setPage(1);
      }} style={{
        ...ghostButton,
        background: tab === 'archivados' ? T.accentSoft : 'transparent',
        color: tab === 'archivados' ? T.accent : T.textMuted
      }} data-id="element-1199" data-mp="3mmpwxglek48h">
          
            Archivados
          </button>}
      </div>

      {filteredRoles.length === 0 ? <EmptyState title="Sin roles definidos" hint="Crea perfiles para controlar qué puede ver y editar cada usuario." icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" data-id="element-1201" data-mp="1jajmyndg1vjs">
          
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" data-id="element-1202" data-mp="19eq7av4fhlxs" />
            </svg>} data-id="element-1200" data-mp="1au2j74ss3gd7" /> : <MotionGrid style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit,minmax(310px,1fr))',
      gap: 14
    }} data-id="element-1203" data-mp="1eci15uomvi4m">
        
          {pageItems.map(rol => <MotionItem key={rol.id} className="getr-card" style={{
        ...glassLight,
        padding: 20
      }} data-id="element-1204" data-mp="224yke8k3xg5i">
          
              <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12
        }} data-id="element-1205" data-mp="1sdazyrlywzdj">
            
                <div style={{
            display: 'flex',
            gap: 12,
            minWidth: 0
          }} data-id="element-1206" data-mp="1xab4gy365u2j">
              
                  <div style={{
              width: 38,
              height: 38,
              borderRadius: 11,
              flexShrink: 0,
              background: T.accentSoft,
              border: `1px solid ${T.accentBorder}`,
              color: T.accent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }} data-id="element-1207" data-mp="2kznajp379m0m">
                
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" data-id="element-1208" data-mp="n6kv8s9cmilu">
                  
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" data-id="element-1209" data-mp="1kw61f86hsg4l" />
                    </svg>
                  </div>
                  <div style={{
              minWidth: 0
            }} data-id="element-1210" data-mp="1b409tzk5k0px">
                
                    <div style={{
                fontSize: 16,
                fontWeight: 700,
                color: T.text
              }} data-id="element-1211" data-mp="165qcpjevaq1o">
                  
                      {rol.nombre}
                    </div>
                    {rol.descripcion ? <div style={{
                marginTop: 5,
                color: T.textSecondary,
                fontSize: 13
              }} data-id="element-1212" data-mp="1ytcjf0je6asr">
                      {rol.descripcion}
                    </div> : null}
                  </div>
                </div>
                <div style={{
            display: 'flex',
            gap: 8,
            flexShrink: 0,
            justifyContent: isMobile ? 'stretch' : 'flex-end'
          }} data-id="element-1213" data-mp="2g2x3cz637gxo">
              
                  <button className="getr-btn-ghost" onClick={() => onEdit(rol.id)} style={{
              ...ghostButton,
              flex: isMobile ? 1 : 'none'
            }} data-id="element-1214" data-mp="2p4oc6cvxc3vr">
                
                    Editar
                  </button>
                  <button onClick={() => onDelete(rol.id)} style={{
              ...ghostButton,
              flex: isMobile ? 1 : 'none',
              color: rol.archivado ? T.info : T.warn,
              borderColor: rol.archivado ? 'rgba(96,165,250,0.3)' : 'rgba(251,191,36,0.3)',
              background: rol.archivado ? 'rgba(96,165,250,0.1)' : 'rgba(251,191,36,0.1)'
            }} data-id="element-1215" data-mp="2yw13929weq0q">
                
                    {rol.archivado ? 'Desarchivar' : 'Archivar'}
                  </button>
                </div>
              </div>
              <div style={{
          marginTop: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }} data-id="element-1216" data-mp="1mhsvy6xgn9pa">
            
                {MODULOS_ACTUALES.map(modulo => {
            const acciones = rol.permisos[modulo] ?? sinPermiso;
            return <div key={modulo} style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? 'minmax(0,1fr) repeat(3,46px)' : 'minmax(0,1fr) repeat(3,60px)',
              alignItems: 'center',
              gap: isMobile ? 5 : 8,
              padding: '8px 0',
              borderTop: `1px solid ${T.border}`
            }} data-id="element-1217" data-mp="1rgcj2ansirj8">
                  
                      <span style={{
                color: T.textSecondary,
                fontSize: 13,
                fontWeight: 600
              }} data-id="element-1218" data-mp="b46vvqq2bnoz">
                    
                        {moduleLabels[modulo] || modulo}
                      </span>
                      {(['leer', 'editar', 'archivar'] as const).map(accion => <span key={accion} title={accion} style={{
                textAlign: 'center',
                padding: isMobile ? '5px 3px' : '5px 7px',
                borderRadius: T.rSm,
                background: acciones[accion] ? T.accentSoft : 'rgba(255,255,255,.04)',
                border: acciones[accion] ? `1px solid ${T.accentBorder}` : `1px solid ${T.border}`,
                color: acciones[accion] ? T.accent : T.textMuted,
                fontSize: isMobile ? 9.5 : 11,
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }} data-id="element-1219" data-mp="3jc6ik4az8f5u">
                      
                            {isMobile ? {
                  leer: 'Ver',
                  editar: 'Edit',
                  archivar: 'Arch'
                }[accion] : accion}
                          </span>)}
                    </div>;
          })}
              </div>
            </MotionItem>)}
        </MotionGrid>}
      {roles.length > 0 && <Pagination page={page} totalPages={totalPages} onChange={setPage} rangeStart={rangeStart} rangeEnd={rangeEnd} total={total} label="roles" data-id="element-1220" data-mp="22f4w9f2kiucf" />}

    </PageShell>;
}